import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees } from '@/api/generated/company-management-user/company-management-user'
import type { EmployeeUserResponse, ServiceModuleModel } from '@/api/model'
import { PageLayout } from '@/components/layout/PageLayout'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageHeaderActions } from '@/components/layout/PageHeaderActions'
import { Button } from '@/components/shadcn/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { FilterRow } from '@/components/common/filters/FilterRow'
import { FilterDateRangePicker } from '@/components/common/filters/FilterDateRangePicker'
import { useTravelExpenseEntries } from './hooks/useTravelExpenseEntries'
import { TravelExpenseExportButton } from './components/TravelExpenseExportButton'
import { TravelExpenseEntriesTable } from './components/TravelExpenseEntriesTable'
import { unwrap } from '@/api/unwrap'

interface Props {
  module: ServiceModuleModel
  onBack: () => void
}

const ALL_EMPLOYEES = 'all'

/** All travel days of the module, filterable by employee and date range. */
export function TravelExpenseAllEntriesPage({ module, onBack }: Props) {
  const { t } = useTranslation(['service-modules-travel-expenses', 'common', 'filters'])
  const [employeeUserId, setEmployeeUserId] = useState<string>()
  const [range, setRange] = useState<{ from?: string; to?: string }>({})

  const employeesQuery = useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees(module.id)
  const employees = useMemo(
    () => unwrap<EmployeeUserResponse[]>(employeesQuery.data) ?? [],
    [employeesQuery.data],
  )

  const entries = useTravelExpenseEntries(module.id, { employeeUserId, from: range.from, to: range.to })

  return (
    <PageLayout scrollEnabled>
      <PageHeader title={t('allTitle')}>
        <PageHeaderActions>
          <TravelExpenseExportButton
            moduleId={module.id}
            filters={{ employeeUserId: employeeUserId, from: range.from, to: range.to }}
          />
          <Button variant="outline" className="h-9 gap-2" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
            {t('buttons.back', { ns: 'common' })}
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <FilterRow>
        <Select
          value={employeeUserId ?? ALL_EMPLOYEES}
          onValueChange={(v) => setEmployeeUserId(v === ALL_EMPLOYEES ? undefined : v)}
        >
          <SelectTrigger className="w-56 h-9!">
            <SelectValue placeholder={t('dropdown.employeePlaceholder', { ns: 'filters' })} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_EMPLOYEES}>{t('dropdown.allEmployees', { ns: 'filters' })}</SelectItem>
            {employees.map((employee) => (
              <SelectItem key={employee.id} value={employee.id ?? ''}>
                {`${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim() || (employee.id ?? '—')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <FilterDateRangePicker from={range.from} to={range.to} onChange={setRange} />
      </FilterRow>

      <TravelExpenseEntriesTable entries={entries} />
    </PageLayout>
  )
}
